import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';

/**
 * Address autocomplete powered by the French government BAN API
 * (api-adresse.data.gouv.fr). Free, no API key, ~26M addresses.
 *
 * Props:
 * - value        : current text value of the address field
 * - onChange     : fn(text) called on each keystroke (kept for controlled form parents)
 * - onSelect     : fn({label, address, postcode, city, lat, lng}) called when user picks a suggestion
 * - placeholder  : input placeholder
 * - required     : forwarded to native input
 * - icon         : whether to render a left MapPin (default true)
 * - inputClassName : extra classes applied to the <Input>
 * - testid       : data-testid prefix
 */
export default function AddressAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Commencez a taper votre adresse...',
  required = false,
  icon = true,
  inputClassName = 'bg-zinc-950 border-zinc-800 text-white pl-10',
  testid = 'address-autocomplete',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=6&autocomplete=1`;
      const res = await fetch(url);
      const data = await res.json();
      const feats = (data?.features || []).map((f) => ({
        label: f.properties?.label || '',
        address: [f.properties?.housenumber, f.properties?.street].filter(Boolean).join(' ') || f.properties?.name || '',
        postcode: f.properties?.postcode || '',
        city: f.properties?.city || '',
        context: f.properties?.context || '',
        lat: f.geometry?.coordinates?.[1],
        lng: f.geometry?.coordinates?.[0],
      })).filter((s) => s.label);
      setSuggestions(feats);
      setOpen(feats.length > 0);
      setActive(-1);
    } catch (_e) {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    if (onChange) onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 220);
  };

  const handlePick = (s) => {
    if (onSelect) onSelect(s);
    if (onChange) onChange(s.label);
    setOpen(false);
    setSuggestions([]);
    setActive(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      handlePick(suggestions[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      {icon && (
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10" size={18} />
      )}
      <Input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        placeholder={placeholder}
        required={required}
        className={inputClassName}
        autoComplete="off"
        data-testid={`${testid}-input`}
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 animate-spin" size={16} />
      )}
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-md shadow-2xl"
          data-testid={`${testid}-list`}
        >
          {suggestions.map((s, idx) => (
            <li
              key={`${s.label}-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); handlePick(s); }}
              onMouseEnter={() => setActive(idx)}
              className={`px-3 py-2 cursor-pointer flex items-start gap-2 ${
                idx === active ? 'bg-red-600/20' : 'hover:bg-zinc-800'
              }`}
              data-testid={`${testid}-option-${idx}`}
            >
              <MapPin className="text-red-500 mt-0.5 shrink-0" size={14} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{s.label}</p>
                {s.context && (
                  <p className="text-xs text-zinc-500 truncate">{s.context}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
